import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../Layout/GRISidebar";
import styles from "../../../styles/gri/general.module.css";
import {
  Box,
  Button,
  Alert,
  Snackbar,
  RadioGroup,
  FormControl,
  Radio,
  FormControlLabel,
} from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import SectionHeading from "../GRI300/GRI308/SectionHeading";
import CustomTextField from "../GRI300/GRI308/CustomTextField";
import { numberError } from "../../../utils/validationMessage";
import ReviewBox from "../../../Components/Common/ReviewBox";
import { currentYear } from "../../../utils/generateYearRanges";

const GRI402 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [formData, setFormData] = useState({
    collectiveBargainingNoticeDetails: {
      days: "",
      unit: "",
    },
    noticePeriodDays: "",
  });
  const [showFields, setShowFields] = useState(true); // Default to "Yes"
  // Add dynamic griReview state
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  // Listen for localStorage changes (in case of multi-tab)
  useEffect(() => {
    const handleStorage = () => {
      setGriReview(JSON.parse(localStorage.getItem("griReview")));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Also update griReview on mount and after navigation
  useEffect(() => {
    setGriReview(JSON.parse(localStorage.getItem("griReview")));
  }, []);

  const handleRadioChange = (event) => {
    setShowFields(event.target.value === "yes");
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?type=GRI400&Id=${id}`);
        if (response?.data?.data?.year < currentYear - 1)
          setDisabled(true);
        console.log(response.data, "resp");
        const data = response.data.data.GRI400?.labourManagement || {};
        setFormData({
          collectiveBargainingNoticeDetails: {
            days: data.collectiveBargainingNoticeDetails?.days || "",
            unit: data.collectiveBargainingNoticeDetails?.unit || "",
          },
          booleanfield: data.booleanfield || false,
          noticePeriodDays: data.noticePeriodDays || "",
        });
        setShowFields(data.booleanfield || false);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    // Remove error for the specific field
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    // Validate required fields in the formData
    if (showFields) {
      // Validate `days` field
      if (!formData.collectiveBargainingNoticeDetails.days) {
        newErrors.days = "This field is required";
        formIsValid = false;
      } else if (
        isNaN(formData.collectiveBargainingNoticeDetails.days) ||
        formData.collectiveBargainingNoticeDetails.days < 0
      ) {
        newErrors.days = numberError;
        formIsValid = false;
      }

      if (
        !formData.collectiveBargainingNoticeDetails.unit ||
        formData.collectiveBargainingNoticeDetails.unit.trim() === ""
      ) {
        newErrors.unit = "This field is required";
        formIsValid = false;
      }
    }

    // Validate `noticePeriodDays` field

    if (isNaN(formData.noticePeriodDays) || formData.noticePeriodDays < 0) {
      newErrors.noticePeriodDays = numberError;
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      if (!validateForm()) return; // Validate form before submission
      const payload = {
        type: "GRI400",
        submitted402: true,
        labourManagement: {
          noticePeriodDays: formData.noticePeriodDays,
          booleanfield: showFields,
          collectiveBargainingNoticeDetails: {
            days: formData.collectiveBargainingNoticeDetails.days,
            unit: formData.collectiveBargainingNoticeDetails.unit,
          },
        },
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/gri400/403-1/${id}`);
      } catch (error) {
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/gri400/403-1/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      {/* <SectionHeading text="2-1 Activities, value chain and other business relationships" /> */}

      <p className={styles.section_heading} style={{ marginBottom: hide ? "-1rem" : "0" }}>
        402 : Labour Management Relations
      </p>

      <p
        className={styles.main_heading}
        style={{ marginBottom: hide ? "-2rem" : "0" }}
      >
        {" "}
        402-1 : Minimum Notice Period regarding operational changes
      </p>
      <SectionHeading
        text="a. What is the standard minimum duration of notice typically given to employees and their representatives before significant operational changes that may substantially impact them are implemented?"
        sx={{ marginTop: "2rem" }}
      />
      <Box sx={{ width: "50%", marginTop: hide ? "-1rem" : "0" }}>
        <CustomTextField
          value={formData.noticePeriodDays}
          placeholder="Days"
          onChange={(e) =>
            handleInputChange("noticePeriodDays", e.target.value)
          }
          error={errors.noticePeriodDays}
          helperText={errors.noticePeriodDays}
          disabled={disabled}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <SectionHeading text="b. Does the organization have collective bargaining agreements in place? If so, are the notice periods and details regarding consultation and negotiation specified within these collective agreements?" />
        <FormControl sx={{ marginLeft: "1rem" }}>
          <RadioGroup
            row
            value={showFields ? "yes" : "no"}
            onChange={handleRadioChange}
            disabled={disabled}
          >
            <FormControlLabel
              value="yes"
              control={<Radio disabled={disabled} />}
              label="Yes"
            />
            <FormControlLabel
              value="no"
              control={<Radio disabled={disabled} />}
              label="No"
            />
          </RadioGroup>
        </FormControl>

        {/* Conditionally Rendered Fields */}
        {showFields && (
          <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
            <Box sx={{ width: "50%" }}>
              <CustomTextField
                placeholder="Days"
                value={formData.collectiveBargainingNoticeDetails.days}
                onChange={(e) =>
                  handleNestedInputChange(
                    "collectiveBargainingNoticeDetails",
                    "days",
                    e.target.value
                  )
                }
                error={errors.days}
                helperText={errors.days}
                disabled={disabled}
              />
            </Box>

            <Box sx={{ width: "50%" }}>
              <CustomTextField
                value={formData.collectiveBargainingNoticeDetails.unit}
                onChange={(e) =>
                  handleNestedInputChange(
                    "collectiveBargainingNoticeDetails",
                    "unit",
                    e.target.value
                  )
                }
                error={errors.unit}
                helperText={errors.unit}
                disabled={disabled}
              />
            </Box>
          </Box>
        )}

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI402;
